import { Drawer } from "@/components/Drawer";
import { useOpsStore } from "../state/opsStore";
import "./OperatorDetailDrawer.css";

/**
 * Phase 18 WU-18.D7 — OperatorDetailDrawer.
 *
 * Slide-in panel shown when a row in OperatorGrid is clicked. Displays
 * the live snapshot (name, current doc, step, kpm, active since). The
 * 24h trend chart pulls from `/api/analytics/operator-trend` and is
 * implemented in WU-18.E (operator performance tab + detail page).
 *
 * For the Ops Console, this drawer is intentionally minimal — it
 * confirms the click target is responsive and links to the full trend
 * view, which is where the chart work lives.
 *
 * Followups #20: chrome migrated from hand-rolled <aside> to the shared
 * <Drawer> primitive. The body still uses local row/label/value classes.
 */
interface Props {
  operatorId: string | null;
  onClose: () => void;
}

export function OperatorDetailDrawer({ operatorId, onClose }: Props) {
  const operator = useOpsStore((s) =>
    operatorId ? s.operators[operatorId] : undefined,
  );

  return (
    <Drawer
      isOpen={Boolean(operatorId)}
      onClose={onClose}
      title={operator?.name ?? operatorId ?? "Operator"}
      width="sm"
      density="compact"
    >
      <div data-testid="ops-operator-drawer">
        {operator ? (
          <>
            <div className="ops-operator-drawer__row">
              <span className="ops-operator-drawer__label">Current doc</span>
              <span className="ops-operator-drawer__value">
                {operator.currentDocId ?? "—"}
              </span>
            </div>
            <div className="ops-operator-drawer__row">
              <span className="ops-operator-drawer__label">Current step</span>
              <span className="ops-operator-drawer__value">
                {operator.currentStep ?? "—"}
              </span>
            </div>
            <div className="ops-operator-drawer__row">
              <span className="ops-operator-drawer__label">KPM (5m)</span>
              <span className="ops-operator-drawer__value">
                {Math.round(operator.kpm5m)}
              </span>
            </div>
            <div className="ops-operator-drawer__row">
              <span className="ops-operator-drawer__label">Active since</span>
              <span className="ops-operator-drawer__value">
                {new Date(operator.activeSince).toLocaleTimeString()}
              </span>
            </div>
          </>
        ) : (
          <div className="ops-operator-drawer__empty">
            Operator is no longer online.
          </div>
        )}
      </div>
    </Drawer>
  );
}
